import { ApiError, backendFetch } from "@softmax/observatory-client/transport";
import { announceSessionChange } from "@softmax/web-features/session/commands";
import { maxAvatarUploadBytes } from "@/lib/upload-limits";
import type { AccountUser, LinkedAccounts } from "./account.types";

export type AccountProfileInput = {
  name: string;
  email: string;
  institution: string | null;
  hasAcceptedTos: boolean;
  consentServiceUpdates: boolean;
  consentMarketing: boolean;
};

export type AccountProfile = {
  name: string | null;
  email: string | null;
  institution: string | null;
  profileCompleted: boolean;
  tosAcceptedAt: string | null;
  tosVersion: string | null;
  consentServiceUpdates: boolean;
  consentMarketing: boolean;
};

type AccountDetails = {
  user: AccountUser;
  linkedAccounts: LinkedAccounts & { githubLogin: string | null };
  githubAuthorization: { login: string } | null;
};

function accountRequest<T>({
  viewerId,
  path,
  init,
}: {
  viewerId: string;
  path: string;
  init: RequestInit;
}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Account-User-Id", viewerId);
  return backendFetch<T>("", path, { ...init, headers }).then(
    (response) => response,
    (error: Error) => {
      if (error instanceof ApiError && error.status === 409)
        announceSessionChange({ kind: "invalidated" });

      throw error;
    },
  );
}

export function readAccountProfile({
  viewerId,
  signal,
}: {
  viewerId: string;
  signal?: AbortSignal | undefined;
}): Promise<AccountDetails> {
  return accountRequest({
    viewerId,
    path: "/api/user",
    init: { signal: signal ?? null },
  });
}

export function saveAccountProfile({
  viewerId,
  profile,
}: {
  viewerId: string;
  profile: AccountProfileInput;
}): Promise<{ user: AccountProfile }> {
  return accountRequest({
    viewerId,
    path: "/api/user",
    init: {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profile),
    },
  });
}

export async function uploadAccountPhoto({
  viewerId,
  image,
}: {
  viewerId: string;
  image: File;
}): Promise<string> {
  if (image.size > maxAvatarUploadBytes)
    throw new Error("File too large. Maximum size is 5MB.");

  const form = new FormData();
  form.append("file", image);
  const response = await accountRequest<{ image: string }>({
    viewerId,
    path: "/api/user/avatar",
    init: { method: "POST", body: form },
  });
  return response.image;
}

export function removeAccountPhoto(viewerId: string): Promise<{ image: null }> {
  return accountRequest({
    viewerId,
    path: "/api/user/avatar",
    init: { method: "DELETE" },
  });
}
